/* Hallmark · component: Banner · genre: modern-minimal · theme: maeiz-tokens */
import React from "react";
import { clsx } from "clsx";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

export type BannerTone = "info" | "success" | "warning" | "danger";

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
    tone?: BannerTone;
    title?: string;
    onDismiss?: () => void;
}

const toneBox: Record<BannerTone, string> = {
    info: "border-secondary/30 bg-secondary/10",
    success: "border-success/30 bg-success/10",
    warning: "border-warning/30 bg-warning/10",
    danger: "border-danger/30 bg-danger/10",
};

const icons = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: AlertCircle };

export const Banner: React.FC<BannerProps> = ({
    tone = "info",
    title,
    onDismiss,
    className,
    children,
    ...props
}) => {
    const Icon = icons[tone];
    return (
        <div
            role="status"
            className={clsx(
                "flex w-full items-start gap-3 border-y px-4 py-3 text-sm text-neutral-dark dark:text-white sm:px-6",
                toneBox[tone],
                className,
            )}
            {...props}
        >
            <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
                {title ? <p className="font-semibold">{title}</p> : null}
                <div className="text-neutral-secondary dark:text-slate-300">{children}</div>
            </div>
            {onDismiss ? (
                <button
                    type="button"
                    onClick={onDismiss}
                    className="rounded-mz-sm p-1 hover:bg-black/5 dark:hover:bg-white/10"
                    aria-label="Dismiss banner"
                >
                    <X size={16} />
                </button>
            ) : null}
        </div>
    );
};
